import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import useSWRMutation from "swr/mutation";
import { selectFileReader } from "commons";
import { formDataFetcher } from "api/swr";
import { toast } from "@/components/ui/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Upload, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEditorStore } from "../../../../store/editorStore";

type StaticUploadRow = {
  id: string;
  name: string;
  preview: string;
  w: number;
  h: number;
  file: File;
};

async function fileToRow(file: File): Promise<StaticUploadRow> {
  const data = (await selectFileReader(file)) as {
    id: string;
    name: string;
    original: string;
    w: number;
    h: number;
  };
  return {
    id: data.id,
    name: data.name,
    preview: data.original,
    w: data.w,
    h: data.h,
    file,
  };
}

export type StaticSkuUploadDialogMode = "create" | "replace";

export interface StaticSkuUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode?: StaticSkuUploadDialogMode;
  /** mode=create 时必填 */
  selectionId?: string;
  /** mode=replace 时必填：更新该 SKU 的图片资源 */
  replaceSkuId?: string | null;
}

/**
 * 静态图统一入口：先选本地文件 → 上传服务端生成资源 ID → 再写入配置。
 * - create：可多选，每张图新建一条带图的静态 SKU
 * - replace：单张，更新当前编辑 SKU 的 original/thumb
 */
export function StaticSkuUploadDialog({
  open,
  onOpenChange,
  mode = "create",
  selectionId,
  replaceSkuId,
}: StaticSkuUploadDialogProps) {
  const { tid } = useParams<{ tid: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [rows, setRows] = useState<StaticUploadRow[]>([]);
  const [uploading, setUploading] = useState(false);
  /** 上传过程中展示「第几张 / 共几张」 */
  const [uploadProgress, setUploadProgress] = useState<{
    index: number;
    total: number;
    fileName: string;
  } | null>(null);

  const addStaticSkuToSelectionWithAssets = useEditorStore(
    (s) => s.addStaticSkuToSelectionWithAssets,
  );
  const updateStaticSkuField = useEditorStore((s) => s.updateStaticSkuField);

  const { trigger: uploadAssets } = useSWRMutation(
    "/spine-templates/upload-static-sku-assets",
    formDataFetcher,
  );

  const isReplace = mode === "replace";

  const reset = () => {
    setRows([]);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDialogOpenChange = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handlePickFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    const listFiles = isReplace ? [files[0]!] : Array.from(files);
    const list = await Promise.all(listFiles.map((f) => fileToRow(f)));
    setRows(list);
  };

  const handleRemove = (id: string) => {
    const next = rows.filter((r) => r.id !== id);
    if (next.length === 0) {
      reset();
      handleDialogOpenChange(false);
      return;
    }
    setRows(next);
  };

  const runUploadToServer = async (file: File) => {
    if (!tid) throw new Error("缺少模板 ID");
    const formData = new FormData();
    formData.append("image", file);
    formData.append("tid", tid);
    return uploadAssets(formData) as Promise<{
      originalAssetId: string;
      thumbAssetId: string;
    }>;
  };

  const handleConfirmReplace = async () => {
    if (!tid || !replaceSkuId || rows.length !== 1 || uploading) return;
    const row = rows[0]!;
    setUploading(true);
    setUploadProgress({ index: 1, total: 1, fileName: row.name });
    try {
      const res = await runUploadToServer(row.file);
      updateStaticSkuField(replaceSkuId, {
        originalAssetId: res.originalAssetId,
        thumbAssetId: res.thumbAssetId,
      });
      toast({ title: "上传成功", description: "已更新原图与缩略图资源" });
      handleDialogOpenChange(false);
    } catch (e: unknown) {
      console.error(e);
      toast({
        variant: "destructive",
        title: "上传失败",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleConfirmCreate = async () => {
    if (!tid || !selectionId || rows.length === 0 || uploading) return;
    const total = rows.length;
    setUploading(true);
    let ok = 0;
    const failedRows: StaticUploadRow[] = [];
    const failedNames: string[] = [];

    try {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]!;
        setUploadProgress({
          index: i + 1,
          total,
          fileName: row.name,
        });
        try {
          const res = await runUploadToServer(row.file);
          const skuId = addStaticSkuToSelectionWithAssets(selectionId, {
            originalAssetId: res.originalAssetId,
            thumbAssetId: res.thumbAssetId,
          });
          if (!skuId) {
            failedRows.push(row, ...rows.slice(i + 1));
            failedNames.push(
              row.name,
              ...rows.slice(i + 1).map((r) => r.name),
            );
            toast({
              variant: "destructive",
              title: "无法写入 SKU",
              description: "请稍后重试或减少单次数量",
            });
            break;
          }
          ok++;
        } catch (e: unknown) {
          failedRows.push(row);
          failedNames.push(row.name);
          console.error(e);
        }
      }
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }

    setRows(failedRows);

    if (failedRows.length === 0) {
      toast({
        title: "添加完成",
        description: `已上传并创建 ${ok} 条静态 SKU，可在详情中配置价格等`,
      });
      handleDialogOpenChange(false);
    } else if (ok > 0) {
      toast({
        variant: "destructive",
        title: "部分失败",
        description: `成功 ${ok} 条，剩余 ${failedRows.length} 条可重试：${failedNames.join(", ")}`,
      });
    } else {
      toast({
        variant: "destructive",
        title: "上传失败",
        description: failedNames.join(", "),
      });
    }
  };

  const canSubmit =
    rows.length > 0 &&
    !!tid &&
    (isReplace ? !!replaceSkuId : !!selectionId);

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {isReplace ? "更换静态图" : "添加静态图"}
          </DialogTitle>
          <DialogDescription>
            {isReplace
              ? "选择一张 jpg / png / webp：先上传到服务端生成资源 ID，再写入当前 SKU（与列表添加流程一致）。"
              : "可选一张或多张图片。每张会先上传生成原图与缩略图资源，再新建对应静态 SKU；之后在详情里配置价格、隐藏等。"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              ref={fileInputRef}
              type="file"
              multiple={!isReplace}
              accept="image/jpeg,image/png,image/webp"
              className="w-auto max-w-full cursor-pointer"
              disabled={uploading}
              onChange={(e) => {
                void handlePickFiles(e.target.files);
                e.target.value = "";
              }}
            />
            {rows.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploading}
                onClick={reset}
              >
                清空
              </Button>
            )}
          </div>

          {rows.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead className="w-24">预览</TableHead>
                  <TableHead>文件</TableHead>
                  <TableHead className="w-28">尺寸</TableHead>
                  {!isReplace && (
                    <TableHead className="w-20 text-right">操作</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={row.id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <img
                        src={row.preview}
                        alt=""
                        className={cn(
                          "h-16 w-16 rounded-md border object-cover bg-muted",
                        )}
                      />
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <div className="truncate text-sm" title={row.name}>
                        {row.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {row.w} × {row.h}
                    </TableCell>
                    {!isReplace && (
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled={uploading}
                          onClick={() => handleRemove(row.id)}
                          title="移除此项"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="flex justify-end gap-2">
            {rows.length > 0 && (
              <Button
                type="button"
                disabled={uploading || !canSubmit}
                className="min-w-[11rem]"
                aria-busy={uploading}
                aria-live="polite"
                onClick={() =>
                  void (isReplace ? handleConfirmReplace() : handleConfirmCreate())
                }
              >
                <Upload className="mr-2 h-4 w-4 shrink-0" />
                <span className="min-w-0 truncate text-left">
                  {uploading ? (
                    uploadProgress ? (
                      <>
                        上传中 第 {uploadProgress.index}/{uploadProgress.total} 张
                        <span className="hidden sm:inline">
                          {" "}
                          · {uploadProgress.fileName}
                        </span>
                      </>
                    ) : (
                      "准备中…"
                    )
                  ) : isReplace ? (
                    "上传并更新"
                  ) : (
                    `上传并创建（${rows.length}）`
                  )}
                </span>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
